/**
 * CloudLighting — the ONE lighting model of a heap cloud, shared by the volumetric field (CloudMeshField) and its far
 * sprites (CloudImpostorLayer) so a cloud keeps its light across the LOD hand-off (2026-10-07, the cloud-realism work:
 * measured against photographs — Atlas `docs/research/2026-10-07-cloud-realism/`).
 *
 * WHAT WAS WRONG. The march lit a cloud with single scattering × a calibration gain, a "powder" term computed from the
 * MARCH STEP's own optical depth (so the brightness changed with the step length — far clouds, marched with stretched
 * steps, were darker), and an ambient picked by HEIGHT in the cloud and added unshadowed at five times the sky's real
 * contribution. The ambient was as bright as the sun's part: a sunlit turret and the underside of the same cloud differed
 * by a factor of two where real cumulus differ by four to six, so every cloud drew as a flat pale cut-out.
 *
 * THE MODEL (radiance per unit of the sun's illuminance at the cloud, before the droplets' albedo):
 *   single(τ, θ)  = e^(−τ) · P(θ)                       one scattering: the forward lobe (the silver lining toward the
 *                                                        sun), a mild back lobe
 *   multi(τ)      = M/4π · Σ wᵢ · e^(−aᵢ·τ)             the high orders, isotropic: three octaves whose extinction falls
 *                                                        (Wrenninge 2015's a^i) — light that diffuses round a turret
 *   τ             = the optical depth from the point toward the sun (the cone march)
 * At a sun-facing skin (τ = 0) the sum is ≈ 0.29 beside the sun's direction and 0.33 down-sun; times the droplets' albedo
 * the caller passes (Atlas: 0.86) that is 0.25–0.28 — a thick cloud's measured reflectance (albedo 0.75–0.88 over π).
 * (M was 2.6 at first: 0.20 after the albedo, a cloud of albedo 0.64 — and, measured in linear light against 51 cumulus
 * photographs, a sunlit skin 3.2–4.4× its sky where theirs is 4.5–4.8×; at 3.2 it is 3.9–5.3×.) On the far side of a
 * turret (τ ≈ 30) it is ≈ 0.007: the sun is gone and the skin shows the AMBIENT alone —
 *   ambient       = mix(ground light, sky light · crevice, the skin's up-facing share)
 * — the sky on what faces up, the lit ground on what faces down (the flat base), darker in the troughs between florets.
 * With the caller feeding the sky's mean radiance and the ground's radiance at gain 1 the shaded skin reads about the
 * blue sky's own brightness, grey: what a cumulus' shaded side and base look like.
 *
 * Pure numbers + plain TSL helpers (never `Fn`-wrapped — the nested-Fn parameter law of this folder).
 */
import { float, exp, max, pow } from 'three/tsl';

type N = any;

/** Forward / back Henyey–Greenstein lobes of the single-scattering phase and the forward lobe's weight. */
export const CLOUD_PHASE_G_FORWARD = 0.8;
export const CLOUD_PHASE_G_BACK = -0.3;
export const CLOUD_PHASE_FORWARD_WEIGHT = 0.65;
/** The multiple-scattering energy relative to an isotropic single scattering (M) and its three octaves (weight, extinction scale). */
export const CLOUD_MS_GAIN = 3.2;
export const CLOUD_MS_OCTAVES: ReadonlyArray<readonly [number, number]> = [[0.55, 0.45], [0.3, 0.18], [0.15, 0.06]];
/** The sky's share of the ambient left in a trough between florets (1 on a crest). */
export const CLOUD_CREVICE_AO = 0.5;

const INV_4PI = 1 / (4 * Math.PI);

/** Henyey–Greenstein (CPU). */
export function hgCpu(cosTheta: number, g: number): number {
  const g2 = g * g;
  return ((1 - g2) / Math.pow(Math.max(1 + g2 - 2 * g * cosTheta, 1e-4), 1.5)) * INV_4PI;
}
/** The single-scattering phase P(θ) (CPU mirror of `cloudPhase`). */
export function cloudPhaseCpu(cosTheta: number): number {
  return hgCpu(cosTheta, CLOUD_PHASE_G_FORWARD) * CLOUD_PHASE_FORWARD_WEIGHT + hgCpu(cosTheta, CLOUD_PHASE_G_BACK) * (1 - CLOUD_PHASE_FORWARD_WEIGHT);
}
/** The multiple-scattering term at an optical depth toward the sun (CPU mirror of `cloudMultiScatter`). */
export function cloudMultiScatterCpu(tau: number): number {
  let s = 0;
  for (const [w, a] of CLOUD_MS_OCTAVES) s += w * Math.exp(-a * Math.max(0, tau));
  return s * CLOUD_MS_GAIN * INV_4PI;
}
/** A cloud skin's radiance per unit sun illuminance (CPU): single + multiple × `ms` (the multiple-scattering weight 0..1). */
export function cloudSunRadianceCpu(tau: number, cosTheta: number, ms = 1): number {
  return Math.exp(-Math.max(0, tau)) * cloudPhaseCpu(cosTheta) + cloudMultiScatterCpu(tau) * ms;
}

const hg = (cosTheta: N, g: number): N => {
  const g2 = g * g;
  return float(1 - g2).div(pow(max(float(1 + g2).sub(cosTheta.mul(2 * g)), float(1e-4)), float(1.5))).mul(INV_4PI);
};
/** The single-scattering phase P(θ) (TSL). */
export function cloudPhase(cosTheta: N): N {
  return hg(cosTheta, CLOUD_PHASE_G_FORWARD).mul(CLOUD_PHASE_FORWARD_WEIGHT).add(hg(cosTheta, CLOUD_PHASE_G_BACK).mul(1 - CLOUD_PHASE_FORWARD_WEIGHT));
}
/** The multiple-scattering term at an optical depth toward the sun (TSL). */
export function cloudMultiScatter(tau: N): N {
  let s: N = float(0);
  for (const [w, a] of CLOUD_MS_OCTAVES) s = s.add(exp(tau.mul(-a)).mul(w));
  return s.mul(CLOUD_MS_GAIN * INV_4PI);
}
